import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Briefcase,
  CreditCard,
  FileText,
  Gift,
  Handshake,
  LayoutDashboard,
  MessagesSquare,
  ListChecks,
  LogOut,
  Receipt,
  Repeat,
  ShieldCheck,
  UserRound,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { fullProfileQuery } from "@/lib/people";
import { useT } from "@/i18n";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { UnreadBadge } from "@/components/unread-badge";

export const Route = createFileRoute("/app")({
  component: AccountLayout,
});

const USER_TABS = [
  { to: "/app", labelKey: "dashboard.layout.tab.overview", icon: LayoutDashboard, exact: true },
  { to: "/app/messages", labelKey: "messaging.title", icon: MessagesSquare },
  { to: "/app/wallets", labelKey: "dashboard.layout.tab.wallets", icon: Wallet },
  { to: "/app/transactions", labelKey: "dashboard.layout.tab.transactions", icon: Receipt },
  { to: "/app/profile", labelKey: "dashboard.layout.tab.profile", icon: UserRound },
  { to: "/app/collabs", labelKey: "dashboard.layout.tab.collabs", icon: Handshake, soon: true },
  { to: "/app/orders", labelKey: "dashboard.layout.tab.orders", icon: ListChecks },
  { to: "/app/billing", labelKey: "dashboard.layout.tab.billing", icon: CreditCard },
  { to: "/app/invoices", labelKey: "dashboard.layout.tab.invoices", icon: FileText },
  { to: "/app/referrals", labelKey: "dashboard.layout.tab.referrals", icon: Gift },
] as const;

const PRO_TABS = [
  { to: "/app/pro", labelKey: "dashboard.layout.tab.pro", icon: Briefcase },
  { to: "/app/messages", labelKey: "messaging.title", icon: MessagesSquare },
  { to: "/app/profile", labelKey: "dashboard.layout.tab.profile", icon: UserRound },
  { to: "/app/wallets", labelKey: "dashboard.layout.tab.wallets", icon: Wallet },
  { to: "/app/billing", labelKey: "dashboard.layout.tab.billing", icon: CreditCard },
  { to: "/app/invoices", labelKey: "dashboard.layout.tab.invoices", icon: FileText },
  { to: "/app/orders", labelKey: "dashboard.layout.tab.orders", icon: ListChecks },
] as const;


function AccountLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();
  const { t } = useT();
  const unreadMessages = useUnreadMessages();

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const { data: profile } = useQuery(fullProfileQuery(user?.id));

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .eq("role", "admin")
        .maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },
  });

  const proMode = profile?.account_mode === "pro";
  const tabs = proMode ? PRO_TABS : USER_TABS;

async function switchMode() {
    if (!user) return;
    // Activating the professional profile costs 99 Heat. If the user hasn't
    // paid yet, route them to the pro dashboard where the activation fee is
    // charged before the professional identity can be enabled.
    const isPro = profile?.account_mode === "pro";
    const next = isPro ? "user" : "pro";
    if (next === "pro" && profile?.pro_enabled !== true) {
      void navigate({ to: "/app/pro" });
      return;
    }
    await supabase.from("profiles").update({ account_mode: next }).eq("id", user.id);
    await queryClient.invalidateQueries({ queryKey: ["full-profile", user.id] });
    void navigate({ to: next === "pro" ? "/app/pro" : "/app" });
  }

  if (loading || !user) {
    return <div className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">{t("dashboard.layout.loading")}</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">
            {proMode ? profile?.pro_name || t("dashboard.layout.defaultProName") : t("dashboard.layout.yourAccount")}
          </h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void switchMode()}>
            <Repeat className="size-4" />
            {proMode ? t("dashboard.layout.switchToUser") : t("dashboard.layout.switchToPro")}
          </Button>
          {isAdmin && (
            <Button asChild variant="outline" size="sm">
              <Link to="/nc/live/adm">
                <ShieldCheck className="size-4" /> {t("dashboard.layout.controlRoom")}
              </Link>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await supabase.auth.signOut();
              void navigate({ to: "/" });
            }}
          >
            <LogOut className="size-4" /> {t("dashboard.layout.signOut")}
          </Button>
        </div>
      </div>

      <nav className="mt-6 flex flex-wrap gap-1 border-b border-border pb-2">
        {tabs.map((tab) => (

          <Link
            key={tab.to}
            to={tab.to}
            activeOptions={{ exact: "exact" in tab ? tab.exact : false }}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            activeProps={{ className: "bg-muted text-foreground" }}
          >
            <tab.icon className="size-4" /> {t(tab.labelKey)}
            {tab.to === "/app/messages" && <UnreadBadge count={unreadMessages} />}
            {"soon" in tab && Boolean(tab.soon) && (
              <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-foreground">
                {t("dashboard.layout.soon")}
              </span>
            )}
          </Link>
        ))}
        {isAdmin && (
          <Link
            to="/nc/live/adm"
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
          >
            <ShieldCheck className="size-4" /> {t("dashboard.layout.admin")}
          </Link>
        )}
      </nav>


      <div className="mt-6">
        <Outlet />
      </div>
    </div>
  );
}
